/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import type {
	MediaPowderBatches,
	MediaPowderBatchInfo,
	PowderLotDetail,
	ValidationErrors,
} from "../../../types/mediaBatchPreparation.types";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";
import { useTranslation } from "react-i18next";
import { PowderConsumption } from "../../../types/mediaBatchCommon.types";

interface Props {
	powderConsumption: PowderConsumption | undefined;
	selectedPowderBatch: MediaPowderBatches | null;
	setSelectedPowderBatch: React.Dispatch<React.SetStateAction<MediaPowderBatches | null>>;
	selectedPowderBatchLot: PowderLotDetail | null;
	setSelectedPowderBatchLot: React.Dispatch<React.SetStateAction<PowderLotDetail | null>>;
	mixingParameters: any;
	setMixingParameters: React.Dispatch<React.SetStateAction<any>>;
	sopRatio?: string;
	setBatchInfo: React.Dispatch<React.SetStateAction<MediaPowderBatchInfo | null>>;
	errors: ValidationErrors;
	setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
}

const MediaPowderSelectionBox = ({
	powderConsumption,
	selectedPowderBatch,
	setSelectedPowderBatch,
	selectedPowderBatchLot,
	setSelectedPowderBatchLot,
	mixingParameters,
	setMixingParameters,
	sopRatio = "0:0",
	setBatchInfo,
	errors,
	setErrors,
	

}: Props) => {
	const { t } = useTranslation();

	// 1. Establish read-only mode strictly based on the prop
	const isReadOnly = !!powderConsumption;

	const [powderBatches, setPowderBatches] = useState<MediaPowderBatches[]>([]);
	const [powderBatchLots, setPowderBatchLots] = useState<PowderLotDetail[]>([]);

	const getPowderBatches = async () => {
		const urlParams = { sort: "id", order: "desc", status: "usable" };
		try {
			const response = await MediaBatchPreparationInitServices.getMediaPowderBatches(urlParams);
			setPowderBatches(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getSelectedBatchLots = async () => {
		if (!selectedPowderBatch) return;

		const urlParams = {
			sort: "id",
			order: "asc",
			batch_id: selectedPowderBatch.id,
		};
		try {
			const response = await MediaBatchPreparationInitServices.getMediaPowderBatchLots(urlParams);
			setPowderBatchLots(response.data);
			setBatchInfo(response.batch_info);
		} catch (error) {
			console.error(error);
		}
	};

	const handleBatchChange = (batchId: string) => {
		const selected = powderBatches.find((b) => b.id === batchId);
		if (selected) {
			setSelectedPowderBatch(selected);
			setMixingParameters({
				...mixingParameters,
				powder_batch_id: selected.id,
			});
			setErrors(prev => ({
				...prev,
				powderBatch: "",
			}));
		}
	};

	const handleLotChange = (lotid: string) => {
		const selected = powderBatchLots.find((l) => l.id === lotid);
		if (selected) {
			setSelectedPowderBatchLot(selected);
			setMixingParameters({
				...mixingParameters,
				powder_lot_id: selected.id,
			});
			setErrors(prev => ({
				...prev,
				powderLot: "",
			}));
		}
	};

	const handlePowderChange = (value: string) => {
		if (!selectedPowderBatchLot) return;
		const powderQty = parseFloat(value);
		const availableStock = parseFloat(selectedPowderBatchLot?.available_stock);

		if (powderQty > availableStock) return;

		const [powderRatio, solutionRatio] = sopRatio.split(":").map(Number);
		let solutionQty = (powderQty / powderRatio) * solutionRatio;

		if (isNaN(solutionQty)) {
			solutionQty = 0;
		}

		setMixingParameters((prev: any) => ({
			...prev,
			powder_quantity_consumed: value,
			solution_volume_used: String(solutionQty),
		}));
		setErrors(prev => ({
			...prev,
			powderQuantity: "",
		}));
		
	};

	// 2. Prevent network calls if we are in view mode
	useEffect(() => {
		if (isReadOnly) return;
		getPowderBatches();
	}, [isReadOnly]);

	useEffect(() => {
		if (isReadOnly) return;
		setSelectedPowderBatchLot(null);
		getSelectedBatchLots();
	}, [selectedPowderBatch, isReadOnly]);

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
			<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
				<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
					01
				</div>
				<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
					{t("mediaBatchInit.preparation.mediaPowderSelection")}
				</h3>
			</div>
			<div className="p-6 grid grid-cols-12 gap-8 items-center">
				<div className="col-span-12 lg:col-span-7 space-y-6">
					<div className="space-y-1.5">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.selectBatch")}  <span className="text-red-500">*</span>
						</label>

						<div className="relative">
							<select
								value={isReadOnly ? "view-batch" : (selectedPowderBatch?.id ?? "")}
								className={`w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-700 outline-none appearance-none ${isReadOnly ? "bg-slate-100 opacity-70 cursor-not-allowed" : "bg-slate-50/50"}`}
								onChange={(e) => handleBatchChange(e.target.value)}
								disabled={isReadOnly}
							>
								{isReadOnly ? (
									<option value="view-batch">{powderConsumption.powder_batch_number}</option>
								) : (
									<>
										<option value="" disabled>
											{t("mediaBatchInit.preparation.selectPowderBatch")}
										</option>
										{powderBatches.map((batch) => (
											<option key={batch.id} value={batch.id}>
												{batch.batch_name}
											</option>
										))}
									</>
								)}
							</select>
							<span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
								expand_more
							</span>
						</div>
						{!isReadOnly && (
							<div className="flex items-center gap-4 text-[8px] font-bold text-slate-400 uppercase">
								<span>
									{t("mediaBatchInit.preparation.expiry")}: {selectedPowderBatch?.expiry_date}
								</span>
								<span>
									{t("mediaBatchInit.preparation.stock")}:{" "}
									<span className="text-red-500">
										{selectedPowderBatch?.available_stock}
									</span>
								</span>
							</div>
						)}
						{errors.powderBatch && <p className="text-[11px] text-red-500">{errors.powderBatch}</p>}
					</div>
					<div className="space-y-1.5">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.selectLot")}  <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<select
								value={isReadOnly ? "view-lot" : (selectedPowderBatchLot?.id ?? "")}
								className={`w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-700 outline-none appearance-none ${isReadOnly ? "bg-slate-100 opacity-70 cursor-not-allowed" : "bg-slate-50/50"}`}
								onChange={(e) => handleLotChange(e.target.value)}
								disabled={isReadOnly}
							>
								{isReadOnly ? (
									<option value="view-lot">{powderConsumption.powder_lot_code}</option>
								) : (
									<>
										<option value="" disabled>
											{t("mediaBatchInit.preparation.selectLot")}
										</option>
										{powderBatchLots.map((lot) => (
											<option key={lot.id} value={lot.id}>
												{lot.lot_code}
											</option>
										))}
									</>
								)}
							</select>
							<span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
								expand_more
							</span>
						</div>
						<div className="flex items-center gap-1 text-[8px] uppercase">
							<div className="flex items-center gap-1 text-emerald-500 font-black">
								<span className="material-symbols-outlined text-[10px]">
									verified
								</span>
								<span>
									{t("mediaBatchInit.preparation.qaStatus")}: Released
								</span>
							</div>

							<span className="ml-3 text-slate-400 font-bold">
								{t("mediaBatchInit.preparation.stock")}:{" "}
								<span className="text-red-500">
									{selectedPowderBatchLot?.available_stock}
								</span>
							</span>
						</div>
						{errors.powderLot && <p className="mt-2 text-[11px] text-red-500">{errors.powderLot}</p>}

					</div>
				</div>
				<div className="col-span-12 lg:col-span-5">
					<div className="bg-[#FBFDFB] border border-emerald-100 rounded-2xl p-6 relative group overflow-hidden">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">
							{t("mediaBatchInit.preparation.quantityTaken")} (GMS) <span className="text-red-500">*</span>
						</label>
						<div className="flex items-center justify-between">
							<span className="text-4xl font-black text-slate-800">
								<input
									type="number"
									value={
										isReadOnly
											? powderConsumption.quantity_used
											: (mixingParameters.powder_quantity_consumed ?? 0.0)
									}
									max={selectedPowderBatchLot?.available_stock}
									min={0}
									readOnly={isReadOnly}
									disabled={isReadOnly}
									onChange={(e) => handlePowderChange(e.target.value)}
									className={`bg-transparent outline-none border-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
										isReadOnly ? "cursor-not-allowed opacity-70" : ""
									}`}
								/>
							</span>
							<div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
								<span className="material-symbols-outlined text-sm font-black">
									check
								</span>
							</div>
						</div>
						<p className="text-[10px] text-slate-300 italic mt-4 font-medium uppercase tracking-tight">
							{t("mediaBatchInit.preparation.accuracy")}
						</p>
						<div className="absolute -bottom-4 -right-4 text-emerald-500/5 group-hover:scale-110 transition-transform">
							<span className="material-symbols-outlined text-8xl">
								balance
							</span>
						</div>
					</div>
					{errors.powderQuantity && <p className="mt-2 text-[11px] text-red-500">{errors.powderQuantity}</p>}
				</div>
			</div>
		</div>
	);
};

export default MediaPowderSelectionBox;
